/**
 * Renders effective custom fields for a task and allows editing values (when canEdit).
 * Ticket-level fields are independent from List View column visibility preferences.
 */

import { useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTaskCustomFields, useUpdateTaskCustomFields, useEffectiveCustomFieldsForList } from '@/hooks/use-custom-fields';
import { formatDisplayDate } from '@/lib/date-format';
import { cn } from '@/lib/utils';
import type { CustomFieldDefinition } from '@/lib/api';

const borderCls = 'border-border';
const bgCard = 'bg-card';

interface CustomFieldsSectionProps {
  /** Omit during task creation; effective list fields use draftValues instead of task API values. */
  taskId?: string;
  /** List ID for this task; used to only show fields that are currently effective for the list (removed/archived columns are excluded) */
  listId: string | undefined;
  canEdit: boolean;
  /** Create-mode values keyed by field key */
  draftValues?: Record<string, unknown>;
  onDraftChange?: (key: string, value: unknown) => void;
}

function formatCustomFieldValue(def: CustomFieldDefinition, value: unknown): string {
  if (value === null || value === undefined) return '—';
  switch (def.type) {
    case 'date':
      try {
        return formatDisplayDate(value as string);
      } catch {
        return String(value);
      }
    case 'checkbox':
      return value ? 'Yes' : 'No';
    case 'select': {
      const options = (def.config?.options as Array<{ value: string; label?: string }>) ?? [];
      const opt = options.find((o) => o.value === value);
      return opt?.label ?? String(value);
    }
    default:
      return String(value);
  }
}

export function CustomFieldsSection({
  taskId,
  listId,
  canEdit,
  draftValues,
  onDraftChange,
}: CustomFieldsSectionProps) {
  const isCreateMode = !taskId;
  const { data: taskFieldsData, isLoading: taskFieldsLoading } = useTaskCustomFields(
    isCreateMode ? undefined : taskId,
  );
  const { data: effectiveFields = [], isLoading: effectiveLoading } = useEffectiveCustomFieldsForList(listId);
  const updateCustomFields = useUpdateTaskCustomFields(isCreateMode ? undefined : taskId);

  const rows: { definition: CustomFieldDefinition; value: unknown }[] = useMemo(() => {
    if (isCreateMode) {
      return effectiveFields.map((def) => ({
        definition: def,
        value: draftValues?.[def.key] ?? null,
      }));
    }

    const valueByFieldId = new Map((taskFieldsData?.fields ?? []).map((f) => [f.definition.id, f.value]));
    if (listId) {
      return effectiveFields.map((def) => ({
        definition: def,
        value: valueByFieldId.get(def.id) ?? null,
      }));
    }

    return (taskFieldsData?.fields ?? []).filter((f) => !f.definition.isArchived);
  }, [draftValues, effectiveFields, isCreateMode, listId, taskFieldsData?.fields]);

  const loading = isCreateMode ? effectiveLoading : listId ? effectiveLoading : taskFieldsLoading;
  if (loading) return null;
  if (!isCreateMode && !listId && (!taskFieldsData || taskFieldsData.fields.length === 0)) return null;
  if (rows.length === 0) return null;

  const handleChange = (def: CustomFieldDefinition, newValue: unknown) => {
    if (isCreateMode) {
      onDraftChange?.(def.key, newValue);
      return;
    }
    updateCustomFields.mutate({ [def.key]: newValue });
  };

  return (
    <div className={cn('space-y-0 border-t', borderCls)}>
      {rows.map(({ definition: def, value }) => (
        <div
          key={def.id}
          className={cn('flex items-center px-4 py-3 border-b', borderCls)}
        >
          <div className="flex items-center gap-3 w-40 shrink-0">
            <Label className="text-sm text-muted-foreground truncate">{def.name}</Label>
          </div>
          <div className="flex-1 min-w-0">
            {!canEdit ? (
              def.type === 'text' && (def.config as { longText?: boolean } | undefined)?.longText ? (
                <span className="text-sm whitespace-pre-wrap">{formatCustomFieldValue(def, value)}</span>
              ) : (
                <span className="text-sm">{formatCustomFieldValue(def, value)}</span>
              )
            ) : def.type === 'checkbox' ? (
              <Checkbox
                checked={Boolean(value)}
                onCheckedChange={(checked) => handleChange(def, checked === true)}
              />
            ) : def.type === 'select' ? (
              <Select
                value={value != null ? String(value) : ''}
                onValueChange={(v) => handleChange(def, v || null)}
              >
                <SelectTrigger className={cn('h-8 max-w-xs', borderCls, bgCard)}>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {((def.config?.options as Array<{ value: string; label?: string }>) ?? []).map(
                    (opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label ?? opt.value}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            ) : def.type === 'date' ? (
              <Input
                type="date"
                value={value ? String(value).slice(0, 10) : ''}
                onChange={(e) => handleChange(def, e.target.value || null)}
                className={cn('h-8 max-w-[180px]', borderCls, bgCard)}
              />
            ) : def.type === 'number' ? (
              <Input
                type="number"
                value={value !== null && value !== undefined ? String(value) : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  handleChange(def, v === '' ? null : Number(v));
                }}
                className={cn('h-8 max-w-[140px]', borderCls, bgCard)}
              />
            ) : def.type === 'text' && (def.config as { longText?: boolean } | undefined)?.longText ? (
              <textarea
                value={value != null ? String(value) : ''}
                onChange={(e) => handleChange(def, e.target.value || null)}
                rows={4}
                className={cn(
                  'w-full min-w-[200px] rounded-md border px-3 py-2 text-sm bg-background',
                  borderCls,
                  bgCard
                )}
              />
            ) : (
              <Input
                type="text"
                value={value != null ? String(value) : ''}
                onChange={(e) => handleChange(def, e.target.value || null)}
                className={cn('h-8 max-w-xs', borderCls, bgCard)}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
